import React, { useEffect, useRef } from 'react';
import type { WindowWithAdSense } from '@gumtree/shared/src/types/window';

import type { TextAdConditions } from '../../util/text-ads-util';
import { AdSenseSlotDivNames } from '../ad-slots';
import { loadAdSenseAds } from './load-ads';
import './AdsenseAd.scss';

type Props = TextAdConditions & {
    slot: string;
    isZeroResults: boolean;
    resultSize?: number;
    query?: string;
    isQA?: boolean;
    style?: React.CSSProperties;
};

const ensureLibrary = (window: Window) => {
    const w = window as unknown as Record<string, any>;
    if (typeof w._googCsa !== 'function') {
        w._googCsa = function () {
            w._googCsa.q = w._googCsa.q || [];
            w._googCsa.q.push(arguments);
        };
        w._googCsa.t = Date.now();

        const existing = window.document.querySelector(
            'script[src*="//www.google.com/adsense/search/ads.js"]'
        );
        if (!existing) {
            const script = window.document.createElement('script');
            script.async = true;
            script.src = 'https://www.google.com/adsense/search/ads.js';
            window.document.head.appendChild(script);
        }
    }
};

const TextAds: React.FC<Props> = ({
    pageType,
    l1Category,
    device,
    currentPage,
    isZeroResults,
    resultSize,
    query,
    isQA,
    slot = 'afscontainer1',
    style,
}) => {
    const containerId = AdSenseSlotDivNames[slot];

    const hasLoadedRef = useRef(false);

    useEffect(() => {
        if (hasLoadedRef.current) {
            return;
        }
        if (typeof window === 'undefined') {
            return;
        }

        // Basic guard similar to existing enable conditions, but not hard-coding here
        // Component consumer controls where to place/when to render

        ensureLibrary(window);

        // Wait for container to be in DOM
        const el = window.document.getElementById(containerId);
        if (!el) {
            return;
        }

        loadAdSenseAds({
            window: window as unknown as WindowWithAdSense,
            pageType,
            l1Category,
            currentPage,
            device,
            isZeroResults,
            isQA: Boolean(isQA),
            query: query || 'for sale',
            slot,
        });

        hasLoadedRef.current = true;
    }, [
        pageType,
        l1Category,
        device,
        currentPage,
        isZeroResults,
        resultSize,
        query,
        isQA,
        containerId,
    ]);

    return <div id={containerId} style={{ display: 'block', width: '100%', minHeight: '200px', ...style }} className='text-ads-slot' />;
};

export default TextAds;
